import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView, useScroll, useSpring } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Download,
  Phone,
  Mail,
  ClipboardList,
  BookOpen,
  Calculator,
  BarChart3,
  TrendingUp,
  X,
  Star,
  Shield,
  Zap,
  Clock,
  Users,
  DollarSign,
  AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";
import { Header } from "../components/shared";
import Footerr from "../components/Footerr/Footerr";
import usePageMeta from "../../seo/usePageMeta";
import { useRegion } from "../../regions/RegionContext";
import { sendTransactionalEmail } from "../../services/email";

// ─── Animated counter hook ─────────────────────────────────────────
function useCounter(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

// ─── Scroll progress bar ───────────────────────────────────────────
function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });
  return (
    <motion.div
      style={{ scaleX, transformOrigin: "left" }}
      className="fixed top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#1b7840] via-[#46B774] to-[#1b7840] z-[9999]"
    />
  );
}

// ─── Stat card ─────────────────────────────────────────────────────
function StatCard({ value, suffix, label, started }) {
  const count = useCounter(value, 1600, started);
  return (
    <div style={{
      background: '#ffffff',
      borderRadius: 12,
      border: '1px solid rgba(27,120,64,0.12)',
      borderTop: '3px solid #1b7840',
      padding: '14px 12px 14px',
      boxShadow: '0 2px 12px rgba(27,120,64,0.07), 0 1px 3px rgba(0,0,0,0.04)',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      height: '100%',
    }}>
      <div style={{
        fontSize: 'clamp(1.6rem, 2.8vw, 2.1rem)',
        fontWeight: 900,
        color: '#1b7840',
        lineHeight: 1,
        letterSpacing: '-0.03em',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}>
        {count}{suffix}
      </div>
      <div style={{ width: 24, height: 2, borderRadius: 2, background: 'rgba(27,120,64,0.25)', flexShrink: 0 }} />
      <div style={{
        fontSize: '0.62rem',
        fontWeight: 600,
        color: '#6b7280',
        lineHeight: 1.3,
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
      }}>
        {label}
      </div>
    </div>
  );
}

// ─── Tool card ─────────────────────────────────────────────────────
const toolCards = [
  {
    icon: ClipboardList,
    color: "#1b7840",
    bg: "#e8f5ee",
    title: "Restaurant Operations Checklist",
    outcome: "Eliminate the chaos of shift-to-shift variability in under a week",
    badge: "Most Popular",
    file: "terces-restaurant-operations-checklist.pdf",
  },
  {
    icon: BookOpen,
    color: "#155a30",
    bg: "#e3f0e8",
    title: "Staff Training Manuals",
    outcome: "Cut onboarding time by 40% and stop quality inconsistencies at the source",
    badge: null,
    file: "terces-staff-training-manual.pdf",
  },
  {
    icon: Calculator,
    color: "#1b7840",
    bg: "#e8f5ee",
    title: "Food Cost Calculator",
    outcome: "Know exactly where your profits are leaking in minutes",
    badge: "Quick Win",
    file: "terces-food-cost-calculator.pdf",
  },
  {
    icon: BarChart3,
    color: "#155a30",
    bg: "#e3f0e8",
    title: "Break-Even Calculator",
    outcome: "Find your true break-even point and set pricing that protects your margins",
    badge: null,
    file: "terces-break-even-calculator.pdf",
  },
  {
    icon: TrendingUp,
    color: "#1b7840",
    bg: "#e8f5ee",
    title: "Operations Assessment",
    outcome: "Get a scored audit of your restaurant's 5 biggest profit drivers",
    badge: "Free Audit",
    file: "terces-operations-assessment.pdf",
  },
];

// All five branded templates, delivered together as the full toolkit.
const toolkitFiles = toolCards.map((t) => t.file);

// ─── FAQ accordion ─────────────────────────────────────────────────
const faqs = [
  {
    q: "I've tried consultants before and it didn't work.",
    a: "Most consultants give you a report and leave. We embed proven systems—SOPs, cost controls, and training frameworks—that your team actually uses. You see measurable change in the first 30 days or we don't charge.",
  },
  {
    q: "I don't have time for this right now.",
    a: "That's exactly why operators call us. The 30-minute strategy call is designed to give you a clear action plan—not create more work. If we can't show you 3 specific profit leaks in 30 minutes, the call is on us.",
  },
  {
    q: "Will this work for my type of restaurant?",
    a: "We've worked across QSR, casual dining, fine dining, cloud kitchens, and multi-unit chains across India and the USA. The frameworks adapt to your format. Over 90% of our clients see margin improvement within 90 days.",
  },
  {
    q: "What does a strategy call actually involve?",
    a: "A focused 30-minute session where we audit your current margins, identify your top 3 profit leaks, and map out a 90-day fix plan. No pitch decks. No fluff. Just clarity.",
  },
];

// ─── Testimonials ──────────────────────────────────────────────────
const testimonials = [
  {
    quote: "Margins were at 8% when we started. We hit 26% within 90 days. The food cost calculator alone saved us ₹2.4L the first month.",
    name: "Rajesh K.",
    role: "Owner, 3-unit casual dining chain, Hyderabad",
    initials: "RK",
  },
  {
    quote: "I'd tried two consultants before Terces. They gave me slide decks. Terces gave me systems my team actually follows.",
    name: "Priya M.",
    role: "Founder, cloud kitchen group, Bangalore",
    initials: "PM",
  },
  {
    quote: "The operations checklist cut our prep errors by 60% in the first week. Didn't know fixing SOPs could move the needle that fast.",
    name: "Arun S.",
    role: "Multi-unit operator, Chennai",
    initials: "AS",
  },
];

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════
export default function ResourcesPage() {
  const { config } = useRegion();
  usePageMeta({
    title: "Free Restaurant Tools & Resources | Terces Solutions",
    description:
      "Free operations checklists, food cost calculators, break-even tools, and training manuals used by successful restaurant operators.",
    path: "/Resources",
  });

  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", restaurantType: "", revenue: "", challenge: "" });
  const [emailData, setEmailData] = useState({ name: "", email: "", phone: "" });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [toolEmailData, setToolEmailData] = useState({ name: "", email: "" });
  const [toolSubmitted, setToolSubmitted] = useState(false);
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: "-100px" });

  // ─── Validation helpers ──────────────────────────────────────────
  const digitsOnly = (v) => (v || "").replace(/\D/g, "");
  // Accept a full national number (10 digits) up to an international
  // number with country code (max 15 digits per E.164).
  const isValidPhone = (v) => {
    const len = digitsOnly(v).length;
    return len >= 10 && len <= 15;
  };
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

  // Absolute URL to a branded template in /public/resources so Brevo can
  // fetch and attach it server-side.
  const resourceUrl = (file) =>
    `${typeof window !== "undefined" ? window.location.origin : "https://terces.io"}/resources/${file}`;

  // Immediately download a branded template in the user's browser.
  const triggerDownload = (file) => {
    if (typeof document === "undefined") return;
    const a = document.createElement("a");
    a.href = `/resources/${file}`;
    a.download = file;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const sendBrevoEmail = ({ subject, htmlContent, to, replyTo, attachment }) =>
    sendTransactionalEmail({ subject, htmlContent, to, replyTo, attachment });

  // Branded HTML shell for the email that delivers templates to the user.
  const deliveryEmailHtml = (name, intro, files) => `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"/></head>
<body style="margin:0;padding:0;background-color:#f4f4f5;font-family:'Segoe UI',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f5;padding:40px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
        <tr><td style="background-color:#1b7840;padding:36px 40px;text-align:center;">
          <h1 style="margin:0;color:#ffffff;font-size:22px;font-weight:700;letter-spacing:-0.02em;">Terces Solutions</h1>
          <p style="margin:8px 0 0;color:rgba(255,255,255,0.85);font-size:13px;">Your free restaurant resources</p>
        </td></tr>
        <tr><td style="padding:36px 40px;">
          <p style="margin:0 0 16px;font-size:16px;color:#111827;font-weight:600;">Hi ${name || "there"},</p>
          <p style="margin:0 0 24px;font-size:15px;color:#374151;line-height:1.6;">${intro}</p>
          ${files
            .map(
              (f) => `<table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:12px;">
            <tr><td style="background:#f9fafb;border-radius:8px;border-left:4px solid #1b7840;padding:14px 18px;">
              <a href="${resourceUrl(f.file)}" style="font-size:15px;color:#1b7840;font-weight:600;text-decoration:none;">⬇ ${f.title}</a>
            </td></tr>
          </table>`
            )
            .join("")}
          <p style="margin:24px 0 0;font-size:14px;color:#6b7280;line-height:1.6;">The templates are attached to this email and also available at the links above. When you're ready to turn these into results, reply to this email or book a free 30-minute strategy call.</p>
        </td></tr>
        <tr><td style="background-color:#f9fafb;border-top:1px solid #e5e7eb;padding:24px 40px;text-align:center;">
          <p style="margin:0;font-size:12px;color:#9ca3af;">Sent by <strong>Terces Solutions</strong> · info@terces.io</p>
          <p style="margin:6px 0 0;font-size:12px;color:#9ca3af;">© ${new Date().getFullYear()} Terces Solutions. All rights reserved.</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body></html>`;

  // Deliver one or more branded templates to the visitor's inbox.
  const deliverTemplates = async ({ name, email, files, intro }) => {
    await sendBrevoEmail({
      subject:
        files.length > 1
          ? "Your Terces Restaurant Profit Toolkit"
          : `Your download: ${files[0].title}`,
      htmlContent: deliveryEmailHtml(name, intro, files),
      to: [{ email, name: name || email }],
      attachment: files.map((f) => ({ url: resourceUrl(f.file), name: f.file })),
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(formData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!isValidPhone(formData.phone)) {
      toast.error("Please enter a valid contact number with at least 10 digits.");
      return;
    }
    try {
      await sendBrevoEmail({
        subject: `Strategy Call Request from ${formData.name}`,
        replyTo: { email: formData.email, name: formData.name },
        htmlContent: `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"/></head>
<body style="margin:0;padding:0;background-color:#f4f4f5;font-family:'Segoe UI',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f5;padding:40px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
        <tr><td style="background-color:#1b7840;padding:36px 40px;text-align:center;">
          <h1 style="margin:0;color:#ffffff;font-size:22px;font-weight:700;">Terces Solutions</h1>
          <p style="margin:8px 0 0;color:rgba(255,255,255,0.8);font-size:13px;">Strategy Call Request</p>
        </td></tr>
        <tr><td style="padding:36px 40px;">
          <p style="margin:0 0 24px;font-size:15px;color:#374151;">A new strategy call has been booked. Here are the details:</p>
          ${[
            ["Full Name", formData.name],
            ["Email Address", formData.email],
            ["Phone Number", formData.phone],
            ["Restaurant Type", formData.restaurantType],
            ["Monthly Revenue", formData.revenue],
            ["Biggest Challenge", formData.challenge],
          ].map(([label, value]) => `
          <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:12px;">
            <tr><td style="background:#f9fafb;border-radius:8px;border-left:4px solid #1b7840;padding:14px 18px;">
              <p style="margin:0;font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;">${label}</p>
              <p style="margin:4px 0 0;font-size:15px;color:#111827;font-weight:600;">${value || "—"}</p>
            </td></tr>
          </table>`).join("")}
          <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:28px;">
            <tr><td align="center">
              <a href="mailto:${formData.email}" style="display:inline-block;background-color:#1b7840;color:#ffffff;text-decoration:none;font-size:14px;font-weight:600;padding:13px 32px;border-radius:8px;">Reply to ${formData.name}</a>
            </td></tr>
          </table>
        </td></tr>
        <tr><td style="background-color:#f9fafb;border-top:1px solid #e5e7eb;padding:24px 40px;text-align:center;">
          <p style="margin:0;font-size:12px;color:#9ca3af;">Submitted via the Resources page at <strong>terces.io</strong></p>
          <p style="margin:6px 0 0;font-size:12px;color:#9ca3af;">© ${new Date().getFullYear()} Terces Solutions. All rights reserved.</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`,
      });
      toast.success("You're booked in! Check your email for confirmation.");
    } catch (_) {
      toast.error("We couldn't submit your request. Please try again or email info@terces.io.");
    }
    setFormSubmitted(true);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(emailData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (emailData.phone && !isValidPhone(emailData.phone)) {
      toast.error("Please enter a valid contact number with at least 10 digits.");
      return;
    }
    const toolkit = toolCards.map((t) => ({ title: t.title, file: t.file }));
    try {
      await deliverTemplates({
        name: emailData.name,
        email: emailData.email,
        files: toolkit,
        intro:
          "Thanks for your interest! Your complete Terces Restaurant Profit Toolkit is attached \u2014 five branded templates used by successful operators to cut costs and grow margins.",
      });
      toast.success("Toolkit sent! Check your inbox for the templates.");
    } catch (_) {
      toast.error("We couldn't email the toolkit. You can still download the tools above.");
    }
    // Best-effort lead notification to the Terces team.
    try {
      await sendBrevoEmail({
        subject: `Resource Download Request from ${emailData.name}`,
        replyTo: { email: emailData.email, name: emailData.name },
        htmlContent: `<p style="font-family:'Segoe UI',Arial,sans-serif;">New toolkit request:</p><ul style="font-family:'Segoe UI',Arial,sans-serif;"><li><strong>Name:</strong> ${emailData.name || "\u2014"}</li><li><strong>Email:</strong> ${emailData.email}</li><li><strong>Phone:</strong> ${emailData.phone || "\u2014"}</li></ul>`,
      });
    } catch (_) {}
    setEmailSubmitted(true);
  };

  const handleToolUnlock = async (e) => {
    e.preventDefault();
    if (!isValidEmail(toolEmailData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (selectedTool?.file) {
      try {
        await deliverTemplates({
          name: toolEmailData.name,
          email: toolEmailData.email,
          files: [{ title: selectedTool.title, file: selectedTool.file }],
          intro: `Here's your ${selectedTool.title}. It's attached as a branded PDF and available at the link below \u2014 ready to use in your restaurant today.`,
        });
        toast.success("Sent! Check your inbox for the PDF.");
      } catch (_) {
        toast.error("We couldn't email the file, but your download will start now.");
      }
      // Hand the visitor the file immediately, regardless of email status.
      triggerDownload(selectedTool.file);
    }
    // Best-effort lead notification to the Terces team.
    try {
      await sendBrevoEmail({
        subject: `Tool Download Request from ${toolEmailData.name}`,
        replyTo: { email: toolEmailData.email, name: toolEmailData.name },
        htmlContent: `<p style="font-family:'Segoe UI',Arial,sans-serif;">New tool request:</p><ul style="font-family:'Segoe UI',Arial,sans-serif;"><li><strong>Name:</strong> ${toolEmailData.name || "\u2014"}</li><li><strong>Email:</strong> ${toolEmailData.email}</li><li><strong>Tool:</strong> ${selectedTool?.title || "\u2014"}</li></ul>`,
      });
    } catch (_) {}
    setToolSubmitted(true);
  };

  const scrollToForm = () => {
    document.getElementById("strategy-call")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTools = () => {
    document.getElementById("lead-magnets")?.scrollIntoView({ behavior: "smooth" });
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
  };

  return (
    <div className="resources-page" style={{ backgroundColor: "var(--primary-bg)" }}>
      <ScrollProgress />
      <Header />
      <main>


      {/* ══════════════════════════════════════════════
          1. HERO SECTION
      ══════════════════════════════════════════════ */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden" style={{ backgroundColor: "#F6F4EF" }}>
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Label */}
            <p className="text-[#1b7840] text-sm font-bold uppercase tracking-wider mb-6">
              Free Tools For Operational Efficiency
            </p>

            {/* Headline with animated underline */}
            <div className="inline-block relative mb-3">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 leading-tight">
                Fix Your Restaurant's Operations{" "}
                <br />& <span style={{ color: "#1b7840" }}>Profit Margins</span>
              </h1>
              <motion.svg
                className="absolute -bottom-2 left-1/2 transform -translate-x-1/2"
                width="320"
                height="12"
                viewBox="0 0 320 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.4 }}
              >
                <path d="M2 10C80 2, 240 2, 318 10" stroke="#1b7840" strokeWidth="3" strokeLinecap="round" fill="none" />
              </motion.svg>
            </div>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mt-8 mb-10">
              Get proven systems, cost calculators, and expert guidance used by
              successful operators across India &amp; the USA.
            </p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-10"
            >
              <motion.button
                onClick={scrollToForm}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
                className="group relative px-8 py-4 bg-[#1b7840] hover:bg-[#155a30] text-white rounded-full font-bold text-base shadow-lg overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2 justify-center">
                  Book a Free Strategy Call
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </motion.button>

              <motion.button
                onClick={scrollToTools}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
                className="px-8 py-4 bg-white hover:bg-gray-50 text-[#1b7840] border-2 border-[#1b7840]/30 hover:border-[#1b7840] rounded-full font-bold text-base transition-all duration-300"
              >
                Get Free Tools
              </motion.button>
            </motion.div>

            {/* Social proof strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap items-center justify-center gap-6 text-gray-500 text-sm"
            >
              {["No fluff. Just systems.", "Ready to use today", "Built for operators"].map((t, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#1b7840]" strokeWidth={2.5} />
                  {t}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          STATS STRIP
      ══════════════════════════════════════════════ */}
      <section ref={statsRef} className="border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 60%, #f9fafb 100%)', padding: '36px 0' }}>
        <div className="max-w-4xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-5 items-stretch">
          {[
            { value: 30, suffix: " days", label: "Time to First Results" },
            { value: 10, suffix: "%", label: "Avg Margin Increase" },
            { value: 90, suffix: " days", label: "Full ROI Timeline" },
            { value: 20, suffix: "%", label: "Reduction in Food Waste" },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <StatCard {...s} started={statsInView} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          2. PAIN AMPLIFICATION
      ══════════════════════════════════════════════ */}
      <section className="py-20 px-6" style={{ background: "var(--secondary-bg)" }}>
        <div className="max-w-4xl mx-auto text-center mb-12">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full bg-red-50 text-red-600 text-xs font-bold uppercase tracking-widest mb-4 border border-red-100"
          >
            Does This Sound Familiar?
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-extrabold text-gray-900"
            style={{ letterSpacing: "-0.02em" }}
          >
            You're working harder than ever—and the numbers still don't add up.
          </motion.h2>
        </div>

        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            {
              icon: DollarSign,
              color: "#dc2626",
              bgColor: "#fef2f2",
              title: "Margins Shrinking Every Month",
              points: ["Rising food & labor costs", "No clear profitability tracking", "Pricing not aligned with real costs"],
            },
            {
              icon: Users,
              color: "#d97706",
              bgColor: "#fffbeb",
              title: "Staff Doing Things Differently Every Shift",
              points: ["No standardized SOPs", "Quality inconsistency", "Training that doesn't stick"],
            },
            {
              icon: AlertTriangle,
              color: "#7c3aed",
              bgColor: "#f5f3ff",
              title: "No Clear Understanding of Food Costs",
              points: ["No standardized recipes", "Waste and leakage", "Menu items priced on guesswork"],
            },
          ].map((pain, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
              className="rounded-2xl p-6 bg-white border border-gray-100 transition-shadow duration-300 cursor-default"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: pain.bgColor }}>
                <pain.icon className="w-6 h-6" style={{ color: pain.color }} strokeWidth={2} />
              </div>
              <h3 className="font-bold text-gray-900 text-base mb-3">{pain.title}</h3>
              <ul className="space-y-1.5">
                {pain.points.map((pt, j) => (
                  <li key={j} className="flex items-start gap-2 text-gray-500 text-sm">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: pain.color }} />
                    {pt}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10 text-gray-700 font-semibold text-lg"
        >
          We built the tools below to fix exactly these problems. <span className="text-[#1b7840]">For free.</span>
        </motion.p>
      </section>

      {/* ══════════════════════════════════════════════
          3. LEAD MAGNETS
      ══════════════════════════════════════════════ */}
      <section id="lead-magnets" className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-1.5 rounded-full bg-[#e8f5ee] text-[#1b7840] text-xs font-bold uppercase tracking-widest mb-4"
            >
              Free Value Assets
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900"
              style={{ letterSpacing: "-0.02em" }}
            >
              Free tools used by successful restaurants
              <br />
              <span className="text-[#1b7840]">to diagnose and fix profitability.</span>
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            {toolCards.map((tool, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -8, boxShadow: "0 24px 48px rgba(27,120,64,0.12)" }}
                className="relative rounded-2xl border border-gray-100 bg-white p-6 flex flex-col transition-all duration-300 group cursor-default"
              >
                {/* Badge */}
                {tool.badge && (
                  <span className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#1b7840] text-white">
                    {tool.badge}
                  </span>
                )}

                {/* Content */}
                <div className="flex-1">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: tool.bg }}
                  >
                    <tool.icon className="w-6 h-6" style={{ color: tool.color }} strokeWidth={2} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-base mb-2">{tool.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{tool.outcome}</p>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100 mt-5 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setSelectedTool(tool)}
                    className="w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 border border-[#1b7840] text-[#1b7840] hover:bg-[#1b7840] hover:text-white transition-all duration-300"
                  >
                    <Download className="w-4 h-4" />
                    Download Free
                  </motion.button>
                </div>
              </motion.div>
            ))}

            {/* CTA Card */}
            <motion.div
              custom={5}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="rounded-2xl p-6 flex flex-col items-center justify-center text-center"
              style={{ background: "linear-gradient(135deg, #1b7840 0%, #155a30 100%)" }}
            >
              <Zap className="w-10 h-10 text-[#4ade80] mb-4" strokeWidth={1.5} />
              <h3 className="font-bold text-white text-lg mb-2">Want all 5 tools at once?</h3>
              <p className="text-white/70 text-sm mb-5">Get the full Terces Restaurant Profit Toolkit delivered to your inbox instantly.</p>
              <button
                onClick={scrollToTools}
                className="px-6 py-2.5 bg-white text-[#1b7840] rounded-full font-bold text-sm hover:bg-gray-50 transition-colors"
              >
                Download Full Toolkit
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Tool Download Modal ── */}
      <AnimatePresence>
        {selectedTool && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
            style={{ backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
            onClick={() => { setSelectedTool(null); setToolSubmitted(false); setToolEmailData({ name: "", email: "" }); }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => { setSelectedTool(null); setToolSubmitted(false); setToolEmailData({ name: "", email: "" }); }}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>

              {!toolSubmitted ? (
                <>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: selectedTool.bg }}>
                    <selectedTool.icon className="w-6 h-6" style={{ color: selectedTool.color }} />
                  </div>
                  <h3 className="text-xl font-extrabold text-gray-900 mb-1">{selectedTool.title}</h3>
                  <p className="text-gray-500 text-sm mb-6">{selectedTool.outcome}</p>
                  <form onSubmit={handleToolUnlock} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Your Name</label>
                      <input
                        required
                        value={toolEmailData.name}
                        onChange={(e) => setToolEmailData({ ...toolEmailData, name: e.target.value })}
                        placeholder="Rajesh Kumar"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1b7840] focus:outline-none focus:ring-2 focus:ring-[#1b7840]/20 text-sm transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Email Address</label>
                      <input
                        required
                        type="email"
                        value={toolEmailData.email}
                        onChange={(e) => setToolEmailData({ ...toolEmailData, email: e.target.value })}
                        placeholder="you@restaurant.com"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1b7840] focus:outline-none focus:ring-2 focus:ring-[#1b7840]/20 text-sm transition-all"
                      />
                    </div>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3.5 bg-[#1b7840] hover:bg-[#155a30] text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Unlock Free Download
                    </motion.button>
                    <p className="text-center text-xs text-gray-400">No spam. Unsubscribe anytime.</p>
                  </form>
                </>
              ) : (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-4"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
                    className="w-16 h-16 rounded-full bg-[#e8f5ee] flex items-center justify-center mx-auto mb-4"
                  >
                    <CheckCircle2 className="w-8 h-8 text-[#1b7840]" />
                  </motion.div>
                  <h3 className="text-xl font-extrabold text-gray-900 mb-2">It's on its way!</h3>
                  <p className="text-gray-500 text-sm mb-6">We've emailed the <strong>{selectedTool.title}</strong> and your download should have started. If it didn't, use the button below.</p>
                  <div className="flex flex-col gap-3">
                    {selectedTool.file && (
                      <a
                        href={`/resources/${selectedTool.file}`}
                        download
                        className="px-6 py-3 border-2 border-[#1b7840] text-[#1b7840] rounded-xl font-bold text-sm hover:bg-[#1b7840] hover:text-white transition-colors flex items-center justify-center gap-2"
                      >
                        <Download className="w-4 h-4" /> Download {selectedTool.title}
                      </a>
                    )}
                    <button
                      onClick={() => { setSelectedTool(null); setToolSubmitted(false); scrollToForm(); }}
                      className="px-6 py-3 bg-[#1b7840] text-white rounded-xl font-bold text-sm hover:bg-[#155a30] transition-colors"
                    >
                      Book My Free Strategy Call →
                    </button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════════════════
          4. AUTHORITY & TRUST
      ══════════════════════════════════════════════ */}
      <section className="py-20 px-6" style={{ background: "var(--secondary-bg)" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-1.5 rounded-full bg-white text-[#1b7840] text-xs font-bold uppercase tracking-widest mb-4 border border-[#1b7840]/20"
            >
              Real Results
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-extrabold text-gray-900"
              style={{ letterSpacing: "-0.02em" }}
            >
              Operators who used these tools saw results in 30 days.
            </motion.h2>
          </div>

          {/* Case study banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl p-8 mb-10 text-white text-center"
            style={{ background: "linear-gradient(135deg, #1b7840 0%, #0d3d1f 100%)" }}
          >
            <p className="text-sm font-semibold text-[#4ade80] uppercase tracking-widest mb-2">Case Study</p>
            <p className="text-xl md:text-2xl font-extrabold leading-snug">
              "Increased margins by <span className="text-[#4ade80]">18%</span> in 90 days—without raising prices."
            </p>
            <p className="text-white/60 text-sm mt-3">Casual dining operator, Hyderabad · Terces engagement, 2025</p>
          </motion.div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm transition-all duration-300"
              >
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-5 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1b7840] flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {t.initials}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{t.name}</div>
                    <div className="text-gray-400 text-xs">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-10 flex flex-wrap justify-center gap-6"
          >
            {[
              { icon: Shield, text: "100% Confidential" },
              { icon: Clock, text: "30-Min Strategy Call" },
              { icon: CheckCircle2, text: "No Commitment Required" },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-600 text-sm font-medium">
                <b.icon className="w-4 h-4 text-[#1b7840]" strokeWidth={2} />
                {b.text}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          5. PROCESS SECTION
      ══════════════════════════════════════════════ */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center mb-14">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full bg-[#e8f5ee] text-[#1b7840] text-xs font-bold uppercase tracking-widest mb-4"
          >
            How It Works
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-extrabold text-gray-900"
            style={{ letterSpacing: "-0.02em" }}
          >
            From chaos to controlled growth in 3 steps.
          </motion.h2>
        </div>

        <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-6 md:gap-0 relative">
          {/* Connector line */}
          <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-[#1b7840] to-[#46B774]" style={{ left: "16.66%", right: "16.66%" }} />

          {[
            { step: "01", title: "Diagnose", sub: "Audit + Tools", desc: "Use our free calculators and checklist to pinpoint exactly where money is leaking from your operations.", color: "#1b7840" },
            { step: "02", title: "Fix", sub: "Systems + Costing", desc: "We build the SOPs, recipe standards, and cost controls your team can actually follow—without consultancy jargon.", color: "#155a30" },
            { step: "03", title: "Scale", sub: "Optimization", desc: "With clean numbers and consistent operations, growth stops being risky. Open new units on a solid foundation.", color: "#0d3d1f" },
          ].map((s, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex-1 text-center px-6 relative"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="w-20 h-20 rounded-full flex flex-col items-center justify-center mx-auto mb-5 text-white font-extrabold shadow-lg"
                style={{ background: `linear-gradient(135deg, ${s.color} 0%, #46B774 100%)` }}
              >
                <span className="text-xs font-semibold opacity-70">STEP</span>
                <span className="text-lg leading-none">{s.step}</span>
              </motion.div>
              <h3 className="text-xl font-extrabold text-gray-900 mb-1">{s.title}</h3>
              <span className="text-xs font-bold text-[#1b7840] uppercase tracking-wider">{s.sub}</span>
              <p className="text-gray-500 text-sm mt-3 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          6. STRATEGY CALL CTA (Primary Conversion)
      ══════════════════════════════════════════════ */}
      <section id="strategy-call" className="py-20 px-6" style={{ background: "var(--primary-bg)" }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: copy */}
            <div>
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="inline-block px-4 py-1.5 rounded-full bg-[#1b7840] text-white text-xs font-bold uppercase tracking-widest mb-5"
              >
                Free Strategy Call
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-6 leading-tight"
                style={{ letterSpacing: "-0.02em" }}
              >
                Get a Clear Plan to Fix Your Restaurant in{" "}
                <span className="text-[#1b7840]">30 Minutes</span>
              </motion.h2>

              <motion.ul
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="space-y-4 mb-8"
              >
                {[
                  { icon: TrendingUp, text: "Identify every profit leak in your current operation" },
                  { icon: CheckCircle2, text: "Get a personalized 90-day improvement roadmap" },
                  { icon: BarChart3, text: "Walk away knowing exactly what to fix first" },
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#e8f5ee] flex items-center justify-center shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-[#1b7840]" strokeWidth={2.5} />
                    </div>
                    <span className="text-gray-700 font-medium text-base">{item.text}</span>
                  </motion.li>
                ))}
              </motion.ul>

              <div className="flex flex-col gap-3 text-sm text-gray-500">
                <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#1b7840]" /> 100% free, no commitment</div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#1b7840]" /> 30 minutes, focused and actionable</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#1b7840]" /> Results or we don't charge</div>
              </div>
            </div>

            {/* Right: form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100"
            >
              {!formSubmitted ? (
                <>
                  <h3 className="text-xl font-extrabold text-gray-900 mb-1">Book My Free Strategy Call</h3>
                  <p className="text-gray-400 text-sm mb-6">We'll call you within 24 hours to schedule.</p>
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    {[
                      { label: "Full Name", field: "name", placeholder: "Rajesh Kumar", type: "text" },
                      { label: "Email Address", field: "email", placeholder: "you@restaurant.com", type: "email" },
                      { label: "Contact Number", field: "phone", placeholder: config.phone.placeholder, type: "tel" },
                    ].map(({ label, field, placeholder, type }) => (
                      <div key={field}>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
                        <input
                          required
                          type={type}
                          value={formData[field]}
                          onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                          placeholder={placeholder}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1b7840] focus:outline-none focus:ring-2 focus:ring-[#1b7840]/20 text-sm transition-all bg-gray-50 focus:bg-white"
                        />
                      </div>
                    ))}

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Restaurant Type</label>
                      <select
                        value={formData.restaurantType}
                        onChange={(e) => setFormData({ ...formData, restaurantType: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1b7840] focus:outline-none focus:ring-2 focus:ring-[#1b7840]/20 text-sm transition-all bg-gray-50 focus:bg-white text-gray-700"
                      >
                        <option value="">Select type...</option>
                        <option>QSR / Fast Food</option>
                        <option>Casual Dining</option>
                        <option>Fine Dining</option>
                        <option>Cloud Kitchen</option>
                        <option>Multi-Unit Chain</option>
                        <option>Café / Bakery</option>
                        <option>Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Monthly Revenue Range</label>
                      <select
                        value={formData.revenue}
                        onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1b7840] focus:outline-none focus:ring-2 focus:ring-[#1b7840]/20 text-sm transition-all bg-gray-50 focus:bg-white text-gray-700"
                      >
                        <option value="">Select range...</option>
                        {config.content.revenueRanges.map((range) => (
                          <option key={range}>{range}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Biggest Challenge Right Now</label>
                      <textarea
                        rows={3}
                        value={formData.challenge}
                        onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
                        placeholder="e.g. food costs are too high, staff turnover, inconsistent quality..."
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1b7840] focus:outline-none focus:ring-2 focus:ring-[#1b7840]/20 text-sm transition-all bg-gray-50 focus:bg-white resize-none"
                      />
                    </div>

                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="group relative w-full py-4 bg-[#1b7840] hover:bg-[#155a30] text-white rounded-xl font-bold text-base transition-colors overflow-hidden"
                    >
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        Book My Free Strategy Call
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    </motion.button>
                    <p className="text-center text-xs text-gray-400">No spam. Your info is 100% private.</p>
                  </form>
                </>
              ) : (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-8"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
                    className="w-20 h-20 rounded-full bg-[#e8f5ee] flex items-center justify-center mx-auto mb-5"
                  >
                    <CheckCircle2 className="w-10 h-10 text-[#1b7840]" />
                  </motion.div>
                  <h3 className="text-2xl font-extrabold text-gray-900 mb-3">You're booked in!</h3>
                  <p className="text-gray-500 leading-relaxed">
                    We'll call you within <strong>24 hours</strong> to confirm your strategy session.
                    Check your email for a confirmation shortly.
                  </p>
                  <p className="text-[#1b7840] font-semibold mt-4 text-sm">While you wait — download your free tools above.</p>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          7. OBJECTION HANDLING (FAQ)
      ══════════════════════════════════════════════ */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-1.5 rounded-full bg-[#e8f5ee] text-[#1b7840] text-xs font-bold uppercase tracking-widest mb-4"
            >
              Common Questions
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-extrabold text-gray-900"
              style={{ letterSpacing: "-0.02em" }}
            >
              Straight answers to what you're probably thinking.
            </motion.h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-gray-100 overflow-hidden bg-gray-50"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-base pr-4">{faq.q}</span>
                  <motion.div
                    animate={{ rotate: openFaq === i ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="shrink-0"
                  >
                    <ChevronDown className="w-5 h-5 text-[#1b7840]" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4">{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          8. EMAIL CAPTURE (Fallback)
      ══════════════════════════════════════════════ */}
      <section
        className="py-20 px-6"
        style={{ background: "linear-gradient(135deg, #0d3d1f 0%, #1b7840 100%)" }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[#4ade80] text-xs font-bold uppercase tracking-widest mb-4">Not Ready to Talk?</p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4" style={{ letterSpacing: "-0.02em" }}>
              Get the tools first.
            </h2>
            <p className="text-white/70 text-base mb-8">
              Enter your details below and we'll send the complete Terces Restaurant Profit Toolkit—5 tools used by successful restaurants—straight to your inbox.
            </p>

            {!emailSubmitted ? (
              <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    required
                    value={emailData.name}
                    onChange={(e) => setEmailData({ ...emailData, name: e.target.value })}
                    placeholder="Your Name"
                    className="px-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-white/50 focus:bg-white/15 text-sm transition-all"
                  />
                  <input
                    required
                    type="email"
                    value={emailData.email}
                    onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                    placeholder="Email Address"
                    className="px-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-white/50 focus:bg-white/15 text-sm transition-all"
                  />
                </div>
                <input
                  value={emailData.phone}
                  onChange={(e) => setEmailData({ ...emailData, phone: e.target.value })}
                  placeholder="Contact Number (optional)"
                  className="px-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-white/50 focus:bg-white/15 text-sm transition-all"
                />
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="group py-4 bg-white text-[#1b7840] rounded-xl font-bold text-base hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  Download Free Toolkit
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <p className="text-white/40 text-xs">No spam. Unsubscribe anytime. Your data is private.</p>
              </form>
            ) : (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white/10 rounded-2xl p-8 border border-white/20"
              >
                <CheckCircle2 className="w-12 h-12 text-[#4ade80] mx-auto mb-4" />
                <h3 className="text-xl font-extrabold text-white mb-2">Toolkit sent!</h3>
                <p className="text-white/70 text-sm">Check your inbox. When you're ready to talk numbers, we're one click away.</p>
                <button
                  onClick={scrollToForm}
                  className="mt-5 px-6 py-3 bg-white text-[#1b7840] rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors"
                >
                  Book a Strategy Call →
                </button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          MINIMAL FOOTER
      ══════════════════════════════════════════════ */}
      <div className="bg-white border-t border-gray-100 py-8 px-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <span>© {new Date().getFullYear()} Terces Consulting. All rights reserved.</span>
          <div className="flex gap-6">
            <a href="mailto:info@terces.io" className="hover:text-[#1b7840] transition-colors flex items-center gap-1.5">
              <Mail className="w-4 h-4" /> info@terces.io
            </a>
            {config.contact.showPhone ? (
              <a href={config.contact.phoneTel} className="hover:text-[#1b7840] transition-colors flex items-center gap-1.5">
                <Phone className="w-4 h-4" /> {config.contact.phoneDisplay}
              </a>
            ) : (
              <a href={config.contact.whatsappLink} target="_blank" rel="noopener noreferrer" className="hover:text-[#1b7840] transition-colors flex items-center gap-1.5">
                <Phone className="w-4 h-4" /> WhatsApp us
              </a>
            )}
          </div>
          <span className="text-xs">Your information is 100% private and secure.</span>
        </div>
      </div>
      </main>
      <Footerr />
    </div>
  );
}
